/**
 * Spatial index of one sheet. Because projects are immutable, `sync` diffs by object
 * reference: only entities that actually changed are re-indexed.
 */
import RBush from 'rbush';
import type { Element, Id, Point, Project, Wire } from '@elec/domain';
import { elementBounds, getSymbol, registerProjectSymbols } from '@elec/symbols';
import { distanceToSegment, wirePolyline, type SymbolLookup } from './geometry.ts';

export interface Bounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

export interface SceneItem extends Bounds {
  id: Id;
  kind: 'element' | 'wire';
  /** Wire polyline, cached. */
  points?: Point[];
  /** Large outlines (rooms): only their border is clickable, so points inside stay selectable. */
  edgeOnly?: boolean;
}

export type Hit = { kind: 'element' | 'wire'; id: Id };

export class Scene {
  private tree = new RBush<SceneItem>();
  private items = new Map<Id, { ref: Element | Wire; item: SceneItem }>();
  private project: Project | undefined;

  readonly sheetId: Id;
  private readonly symbols: SymbolLookup;

  constructor(sheetId: Id, symbols: SymbolLookup = getSymbol) {
    this.sheetId = sheetId;
    this.symbols = symbols;
  }

  get size(): number {
    return this.items.size;
  }

  sync(project: Project): void {
    registerProjectSymbols(project.symbols);
    const prev = this.project;
    this.project = project;
    if (prev === project) return;
    const seen = new Set<Id>();
    const toInsert: SceneItem[] = [];
    // Elements first so that wire end points resolve against current positions.
    const movedElements = new Set<Id>();
    for (const el of Object.values(project.elements)) {
      if (el.sheetId !== this.sheetId) continue;
      seen.add(el.id);
      const cur = this.items.get(el.id);
      if (cur?.ref === el) continue;
      if (cur) this.tree.remove(cur.item);
      movedElements.add(el.id);
      const item = this.elementItem(el);
      this.items.set(el.id, { ref: el, item });
      toInsert.push(item);
    }
    for (const w of Object.values(project.wires)) {
      if (w.sheetId !== this.sheetId) continue;
      seen.add(w.id);
      const cur = this.items.get(w.id);
      const endsMoved =
        (w.from.kind === 'port' && movedElements.has(w.from.elementId)) ||
        (w.to.kind === 'port' && movedElements.has(w.to.elementId));
      if (cur?.ref === w && !endsMoved) continue;
      if (cur) this.tree.remove(cur.item);
      const item = this.wireItem(project, w);
      if (!item) {
        this.items.delete(w.id);
        continue;
      }
      this.items.set(w.id, { ref: w, item });
      toInsert.push(item);
    }
    for (const [id, cur] of this.items) {
      if (!seen.has(id)) {
        this.tree.remove(cur.item);
        this.items.delete(id);
      }
    }
    // Bulk load is much faster than one-by-one inserts for the initial build.
    if (this.tree.all().length === 0) this.tree.load(toInsert);
    else for (const it of toInsert) this.tree.insert(it);
  }

  query(b: Bounds): SceneItem[] {
    return this.tree.search(b);
  }

  bounds(): Bounds | undefined {
    const all = this.tree.all();
    if (all.length === 0) return undefined;
    const b = { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity };
    for (const i of all) {
      b.minX = Math.min(b.minX, i.minX);
      b.minY = Math.min(b.minY, i.minY);
      b.maxX = Math.max(b.maxX, i.maxX);
      b.maxY = Math.max(b.maxY, i.maxY);
    }
    return b;
  }

  /** Topmost hit at a sheet point; elements win over wires. `tol` in sheet units. */
  hitTest(p: Point, tol: number): Hit | undefined {
    const candidates = this.tree.search({
      minX: p.x - tol,
      minY: p.y - tol,
      maxX: p.x + tol,
      maxY: p.y + tol,
    });
    let wireHit: Hit | undefined;
    let roomHit: Hit | undefined;
    let best = Infinity;
    for (const c of candidates) {
      if (c.kind === 'element') {
        const inside = p.x >= c.minX && p.x <= c.maxX && p.y >= c.minY && p.y <= c.maxY;
        if (c.edgeOnly) {
          // Rooms: only the border and the name label (top-left) are clickable.
          const nearEdge =
            inside &&
            (p.x - c.minX < tol * 2 ||
              c.maxX - p.x < tol * 2 ||
              p.y - c.minY < tol * 2 ||
              c.maxY - p.y < tol * 2 ||
              (p.y - c.minY < 9 && p.x - c.minX < 30));
          if (nearEdge) roomHit ??= { kind: 'element', id: c.id };
          continue;
        }
        if (inside) return { kind: 'element', id: c.id };
      } else if (c.points) {
        for (let i = 0; i < c.points.length - 1; i++) {
          const d = distanceToSegment(p, c.points[i]!, c.points[i + 1]!);
          if (d <= tol && d < best) {
            best = d;
            wireHit = { kind: 'wire', id: c.id };
          }
        }
      }
    }
    return wireHit ?? roomHit;
  }

  private elementItem(el: Element): SceneItem {
    const def = this.symbols(el.symbolId);
    const b = def ? elementBounds(def, el) : { minX: el.x, minY: el.y, maxX: el.x + 5, maxY: el.y + 5 };
    return { id: el.id, kind: 'element', ...b, ...(def?.room ? { edgeOnly: true } : {}) };
  }

  private wireItem(project: Project, w: Wire): SceneItem | undefined {
    const pts = wirePolyline(project, w, this.symbols);
    if (!pts) return undefined;
    const xs = pts.map((q) => q.x);
    const ys = pts.map((q) => q.y);
    return {
      id: w.id,
      kind: 'wire',
      points: pts,
      minX: Math.min(...xs),
      minY: Math.min(...ys),
      maxX: Math.max(...xs),
      maxY: Math.max(...ys),
    };
  }
}
